import {
  ClosedGroupData,
  ColumnData,
  Player,
  PlayerDragData,
} from "../../types/types";
import {
  getPlayerAge,
  getPlayerArchetype,
  getPlayerContractMonths,
  getPlayerPosition,
  getPlayerTeamName,
} from "../../utils/manualPlayerUtils";

import { selectCardReportSignal } from "../../utils/cardReportSignal";
import { outcomeLabel } from "../../utils/outcomeLabels";
import { computeDaysInStage, isStageStale } from "../../utils/stageTime";

import type {
  KanbanCard,
  KanbanCardOutcome,
  KanbanPhase,
} from "@/generated/graphql";
import { CurrencyCode, KanbanSourceType } from "@/generated/graphql";
import { formatGeminiValuationRange } from "@/utils/formatCurrencyShort";
import { formatGPR } from "@/utils/numberFormatting";

export const DEFAULT_PHASE_NAMES = [
  "Discovery",
  "Active Monitoring",
  "Deep Scouting",
  "Evaluation",
  "Negotiation",
  "Decision",
];

/**
 * A card carries `rank` on the wire (`KanbanCardFragment`), but every local
 * fixture and optimistic stand-in predates that selection, so treat it as
 * possibly-absent at runtime regardless of what the generated type asserts.
 */
type CardOrdering = Pick<KanbanCard, "position"> & {
  readonly rank?: number | null;
};

/**
 * Read the ordering key a card should be sorted by for the CURRENT VIEWER.
 * @remarks
 * SE-5493. `position` is the board's SHARED order — every user of a board sees
 * the same value. `rank` is the viewer-scoped 1-based ordinal the backend
 * resolves per request: for a PIPELINE board it comes from that viewer's
 * private `user_kanban_card_order` row, falling back to the shared position
 * when the viewer has never reordered the phase. ROSTER and WATCHLIST boards
 * have no private ordering, so they keep sorting by the shared `position`.
 *
 * Sorting a PIPELINE column by `position` (the pre-SE-5493 behaviour) is
 * exactly the reported bug: `reorderCardsInPhase` correctly persists the
 * viewer's private order, then the re-read renders the shared order back.
 *
 * The `rank` fallback keeps a card that has no rank yet — an optimistic
 * stand-in, or a cache entry written before the fragment selected the field —
 * interleaved sensibly instead of collapsing to 0. It mirrors the backend's own
 * `positions.get(id) ?? userPosition ?? position` resolution, shifted by one
 * because `rank` is 1-based and `position` is 0-based.
 * @param card - Card to derive an ordering key for
 * @param sourceType - Board source type; only PIPELINE uses viewer-private rank
 * @returns The numeric key to sort ascending by
 */
export const getCardOrderKey = (
  card: CardOrdering,
  sourceType?: KanbanSourceType | null
): number =>
  sourceType === KanbanSourceType.Pipeline
    ? (card.rank ?? (card.position || 0) + 1)
    : card.position || 0;

/**
 * Sort cards into the order the current viewer should see them in.
 * @param cards - Cards belonging to a single phase
 * @param sourceType - Board source type; only PIPELINE uses viewer-private rank
 * @returns A new array in ascending viewer order
 */
export const sortCardsForViewer = <T extends CardOrdering>(
  cards: T[],
  sourceType?: KanbanSourceType | null
): T[] =>
  [...cards].sort(
    (a, b) => getCardOrderKey(a, sourceType) - getCardOrderKey(b, sourceType)
  );

/**
 * The per-stage signals `convertCardToPlayer` needs to derive the stale flag
 * (SE-5497). Kept to just the two fields so the pure conversion never depends
 * on the full phase object and stays trivially testable.
 */
export type StagePhaseTimeTarget = Pick<
  KanbanPhase,
  "timeTargetDays" | "isTerminal"
>;

/**
 * Convert player data from KanbanCard format to Player format.
 * Falls back to dateOfBirth and positions[0].name for manual players
 * that lack bioData.age and bioData.roleArchetype.
 * @param card - The kanban card containing player data to convert
 * @param currencyCode - Currency code for formatting contract values (defaults to EUR)
 * @param sourceType - Board source type; on PIPELINE the viewer's server-resolved `rank` is carried onto the player so both renderers badge the same number (SE-5493)
 * @param phase - The card's stage, supplying `timeTargetDays`/`isTerminal` for the SE-5497 stale flag. Omitted for the orphan/closed buckets where no live target applies.
 * @returns Player object with formatted display values for name, GPR, contract, and tags
 */
export const convertCardToPlayer = (
  card: KanbanCard,
  currencyCode: CurrencyCode = CurrencyCode.Eur,
  sourceType?: KanbanSourceType | null,
  phase?: StagePhaseTimeTarget
): Player => {
  const player = card.player;
  // SE-5497: days-in-stage + stale flag render on PIPELINE boards only
  // (decision 3). ROSTER/WATCHLIST leave both undefined so their cards are
  // unchanged. Days are elapsed-ms 24h blocks (TZ-independent); a card is stale
  // only against a positive target and never when closed/terminal (decision 5).
  const isPipeline = sourceType === KanbanSourceType.Pipeline;
  const daysInStage = isPipeline
    ? computeDaysInStage(card.enteredStageAt)
    : undefined;
  const isStale = isPipeline
    ? isStageStale({
        daysInStage: daysInStage ?? null,
        timeTargetDays: phase?.timeTargetDays ?? null,
        isClosed: isCardClosed(card) || Boolean(phase?.isTerminal),
      })
    : false;
  return {
    id: player?.id ?? "",
    // SE-5504: the CARD id, carried alongside the playerId so the preview
    // drawer can scope the audit trail to this one card server-side.
    cardId: card.id,
    name: player?.name ?? "Unknown",
    archetype: player ? getPlayerArchetype(player, "Unknown") : "Unknown",
    age: player
      ? (player.bioData?.age ??
        (player.dateOfBirth ? getPlayerAge(player) : undefined))
      : undefined,
    gpr:
      player?.bioData?.gpr != null ? formatGPR(player.bioData.gpr) : undefined,
    contractValue:
      formatGeminiValuationRange(
        player?.bioData?.minGeminiPlayerValuation,
        player?.bioData?.maxGeminiPlayerValuation,
        currencyCode
      ) ?? "N/A",
    contractMonths: player ? getPlayerContractMonths(player) : null,
    agentName: player?.bioData?.playerAgent || "N/A",
    teamName: player ? getPlayerTeamName(player, "Free Agent") : "Free Agent",
    playerAgent: player?.bioData?.playerAgent ?? undefined,
    tags: (() => {
      const backendTags = card.tags || [];

      return (
        backendTags.map(tag => ({
          id: tag.id,
          name: tag.name,
          color: tag.color || "#3B82F6",
          createdAt: tag.createdAt,
          usageCount: tag.cardCount,
        })) || []
      );
    })(),
    bioData: player?.bioData || undefined,
    // SE-5493: PIPELINE boards badge the viewer's server-resolved rank rather
    // than a client-side ordinal, so the Kanban badge and the Table's Rank
    // column read the same number. ROSTER/WATCHLIST leave this undefined and
    // keep the ordinal derivation their consumers already apply.
    rank:
      sourceType === KanbanSourceType.Pipeline
        ? (card.rank ?? undefined)
        : undefined,
    // SE-5497: pipeline-only time-in-stage signals (undefined elsewhere).
    daysInStage,
    isStale,
    // SE-5492: avatar source. Null for every player today (SE-5538 is schema
    // plumbing only) — the card renders initials, which is correct, not a gap.
    imageUrl: player?.imageUrl ?? null,
    // SE-5492: explicit position. `archetype` above is a ROLE archetype and is
    // not a position, so it cannot satisfy the position field on its own.
    position: player ? getPlayerPosition(player) : undefined,
    // SE-5492: reports count + verdict, derived by the shared selector so the
    // card and the preview drawer apply the same "latest report" rule.
    ...selectCardReportSignal(player),
    // SE-6490: the CARD's last-updated timestamp, driving the footer's
    // "Updated 2d ago". Deliberately the card's and not the player's — the
    // footer reports when this pipeline entry last changed.
    updatedAt: card.updatedAt ?? null,
    // SE-6866: who last EDITED the card, completing that footer with `· Hugo`.
    // Carried as the raw id and resolved to a name at the card
    // (`useCardEditorName`); null whenever no human has edited the card since
    // the field shipped, which renders the time alone.
    lastEditedByUserId: card.lastEditedByUserId ?? null,
    // SE-6554: the Table's Club flag. `bioData.leagueCountry` is the country of
    // the player's CURRENT LEAGUE — i.e. the club's country, which is what the
    // design's flag means. It is now selected by `ListKanbanCards` as a scalar
    // on the bioData object that query already reads.
    clubCountry: player?.bioData?.leagueCountry ?? undefined,
    // SE-6554: the Table's List column, LIVE since 2026-08-07 — `ListKanbanCards`
    // now selects `watchlists { id name }` (see the shape/complexity note on
    // that selection). Players on no watchlist come back as an empty array and
    // the cell renders blank, which is Raniel's ruling: never "- None".
    watchlists: player?.watchlists ?? undefined,
  };
};

/**
 * A card is "closed" once a terminal outcome has been recorded on it
 * (SE-5496). The backend sets `outcome` and `closedAt` together on close and
 * clears both on reopen, so `outcome` is the authoritative closed signal;
 * `closedAt` is a defensive fallback for any partial cache write.
 * @param card - The card to test.
 * @returns `true` when the card has been closed into a terminal stage.
 */
export const isCardClosed = (
  card: Pick<KanbanCard, "outcome" | "closedAt"> | null | undefined
): boolean => Boolean(card && (card.outcome != null || card.closedAt != null));

/**
 * SE-6489: placeholder copy for an empty stage column, verbatim from the
 * approved design (Figma node 80:14461 in the `Column-Pipeline` component).
 * Deliberately does NOT interpolate the stage name.
 */
export const EMPTY_STAGE_PLACEHOLDER = "Drag players here";

/**
 * Generate ColumnData from phases and cards
 * @remarks
 * SE-5496: CLOSED cards are excluded from the normal stage columns — they live
 * in the Closed area (see {@link generateClosedGroups}) instead, even though
 * their `kanbanPhase` is the terminal stage they were closed into. A terminal
 * stage therefore renders as an empty column while its cards appear grouped by
 * outcome in the Closed area.
 * @param phases - Array of kanban phases to organize into columns
 * @param cards - Array of kanban cards to distribute across phases
 * @param currencyCode - Currency code for formatting player contract values (defaults to EUR)
 * @param sourceType - Board source type; PIPELINE orders each phase by the viewer's private `rank`, ROSTER/WATCHLIST by the shared `position` (SE-5493)
 * @returns Array of ColumnData objects with players in viewer order within each phase
 */
export const generateColumnsData = (
  phases: KanbanPhase[],
  cards: KanbanCard[],
  currencyCode: CurrencyCode = CurrencyCode.Eur,
  sourceType?: KanbanSourceType | null
): ColumnData[] =>
  phases.map(phase => {
    const phaseCards = sortCardsForViewer(
      cards.filter(
        card => card?.kanbanPhase?.id === phase.id && !isCardClosed(card)
      ),
      sourceType
    );

    const players = phaseCards
      .filter(card => card?.player)
      .map(card => convertCardToPlayer(card, currencyCode, sourceType, phase));

    return {
      id: phase.id,
      title: phase.name,
      count: players.length,
      players,
      isEmpty: players.length === 0,
      // SE-6489: the design's empty-column placeholder reads exactly "Drag
      // players here" (Figma text node 80:14461) — the stage name is already
      // in the header directly above it, so interpolating it here restated it.
      emptyText: EMPTY_STAGE_PLACEHOLDER,
    };
  });

/**
 * Synthetic column id for cards that resolve to no board phase (SE-5491 AC2).
 * @remarks
 * Never used as a real phase id: `generateColumnsData` derives its columns
 * exclusively from `phases`, so this id can never collide with one.
 */
export const UNSTAGED_COLUMN_ID = "__unstaged__";

/** Display title of the synthetic unstaged bucket in the Table view. */
export const UNSTAGED_COLUMN_TITLE = "Unassigned";

/**
 * Collect cards that belong to no rendered phase into a synthetic column.
 * @remarks
 * SE-5491 AC2 — defence in depth. `generateColumnsData` builds one column per
 * phase and drops any card whose `kanbanPhase.id` matches none of them, so a
 * stage-less card would vanish without a trace. The dev schema makes that
 * unreachable today (`KanbanCard.kanbanPhaseId: ID!`,
 * `KanbanCard.kanbanPhase: KanbanPhase!`), but a phase deleted mid-session, a
 * partial cache write, or a future nullable schema could all produce one.
 *
 * The Kanban board deliberately does NOT render this bucket — no "No stage"
 * column ever appears there. The Table view appends it instead, so an orphan
 * card is surfaced to the user rather than silently dropped.
 * @param phases - Array of kanban phases backing the rendered columns
 * @param cards - Array of kanban cards to check for a resolvable phase
 * @param currencyCode - Currency code for formatting player contract values (defaults to EUR)
 * @returns A synthetic ColumnData holding the orphaned players, or null when every card resolves to a phase
 */
export const generateUnstagedColumnData = (
  phases: KanbanPhase[],
  cards: KanbanCard[],
  currencyCode: CurrencyCode = CurrencyCode.Eur
): ColumnData | null => {
  const phaseIds = new Set(phases.map(phase => phase.id));

  // Deliberately ordered by the SHARED `position` and deliberately NOT carrying
  // a server rank, even on a PIPELINE board (SE-5493). Both `position` and
  // `rank` are phase-scoped, and this bucket mixes cards from different (or
  // deleted) phases, so neither key is comparable across its members — but the
  // shared position at least never yields the duplicate 1,1,2 badges that
  // phase-scoped ranks would. Leaving `rank` undefined lets the Table fall back
  // to a contiguous ordinal over this bucket, which is the only number that
  // means anything here.
  const players = cards
    .filter(card => card?.player && !phaseIds.has(card?.kanbanPhase?.id ?? ""))
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(card => convertCardToPlayer(card, currencyCode));

  if (players.length === 0) return null;

  return {
    id: UNSTAGED_COLUMN_ID,
    title: UNSTAGED_COLUMN_TITLE,
    count: players.length,
    players,
    isEmpty: false,
    emptyText: "",
  };
};

/**
 * Group closed cards into the collapsed "Closed" area (SE-5496).
 * @remarks
 * Ordering (the spec left this unspecified — this is the chosen default,
 * documented for the reviewer):
 *
 * 1. GROUPS are ordered by the board template's `validOutcomes` — the same
 *    ordered, template-scoped set the terminal-outcome selector renders from.
 *    An outcome present on a card but absent from `validOutcomes` (a legacy or
 *    renamed value) is appended after the known groups so no closed card is
 *    ever dropped.
 * 2. CARDS within a group are ordered by the viewer key
 *    ({@link getCardOrderKey}) — `rank` on a PIPELINE board, `position`
 *    elsewhere — matching how the stage columns order.
 *
 * Only groups with at least one card are returned, so an empty outcome never
 * renders a stray zero-count header.
 * @param cards - All board cards (open and closed); closed ones are selected here.
 * @param validOutcomes - The board template's ordered valid outcome set.
 * @param currencyCode - Currency code for formatting player contract values (defaults to EUR).
 * @param sourceType - Board source type; selects the within-group ordering key (SE-5493).
 * @returns The non-empty closed groups in `validOutcomes` order.
 */
export const generateClosedGroups = (
  cards: KanbanCard[],
  validOutcomes: KanbanCardOutcome[],
  currencyCode: CurrencyCode = CurrencyCode.Eur,
  sourceType?: KanbanSourceType | null
): ClosedGroupData[] => {
  const closedCards = cards.filter(card => card?.player && isCardClosed(card));

  // Outcome order: template order first, then any stray outcomes appended in
  // first-seen order so a renamed/legacy outcome still surfaces its cards.
  // Built immutably (no in-place push): spreading a Set dedupes while keeping
  // insertion order.
  const strayOutcomes = closedCards
    .map(card => card.outcome)
    .filter(
      (outcome): outcome is KanbanCardOutcome =>
        outcome != null && !validOutcomes.includes(outcome)
    );
  const seenOrder: KanbanCardOutcome[] = [
    ...validOutcomes,
    ...new Set(strayOutcomes),
  ];

  return seenOrder
    .map(outcome => {
      const groupCards = sortCardsForViewer(
        closedCards.filter(card => card.outcome === outcome),
        sourceType
      );
      const entries = groupCards.map(card => ({
        cardId: card.id,
        player: convertCardToPlayer(card, currencyCode, sourceType),
      }));
      return {
        outcome,
        label: outcomeLabel(outcome),
        count: entries.length,
        entries,
      };
    })
    .filter(group => group.count > 0);
};

/**
 * Create optimistic card for immediate UI updates
 * @param playerId - Unique identifier of the player being added to the kanban board
 * @param phaseId - Target phase ID where the card should be placed
 * @param playerData - Player information object containing name, team, and stats
 * @param phases - Array of all kanban phases for phase lookup
 * @param cards - Existing kanban cards used to calculate position in target phase
 * @returns Temporary card object with generated ID for immediate UI rendering before server confirms
 */
export const createOptimisticCard = (
  playerId: string,
  phaseId: string,
  playerData: PlayerDragData,
  phases: KanbanPhase[],
  cards: KanbanCard[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Return type is intentionally `any` because this creates an optimistic response that doesn't fully match the KanbanCard GraphQL type (missing required fields like __typename, cards, createdAt)
): any => {
  const tempCardId = `temp-${Date.now()}-${playerId}`;
  const targetPhase = phases.find(p => p.id === phaseId);

  return {
    id: tempCardId,
    position: cards.filter(c => c.kanbanPhase.id === phaseId).length,
    notes: null,
    kanbanPhaseId: phaseId,
    // SE-5497: a just-added card entered its stage now, so days-in-stage reads
    // 0 optimistically until the server round-trips the canonical timestamp.
    enteredStageAt: new Date().toISOString(),
    kanbanPhase: {
      id: phaseId,
      name: targetPhase?.name || "Unknown Phase",
    },
    player: {
      id: playerId,
      // Backend-canonical name threaded through from the drag payload
      // (SE-5469); no client-side assembly from firstName/lastName.
      name: playerData.playerName || playerData.name || "Loading...",
      firstName: playerData.firstName || "",
      lastName: playerData.lastName || "",
      team: playerData.teamName
        ? { id: "temp", name: playerData.teamName }
        : null,
      bioData: {
        ggPlayerKey: playerId,
        age: playerData.age,
        gpr: playerData.gpr,
        roleArchetype: playerData.archetype,
        minGeminiPlayerValuation: null,
        maxGeminiPlayerValuation: null,
        contractExpires: null,
      },
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Calculate new position for card movement
 * @remarks
 * Ordering-agnostic by construction: only the target phase's CARD COUNT is
 * read, so whether that phase is ordered by the shared `position` or by the
 * viewer's private `rank` (SE-5493) cannot change the result. The
 * position-ascending sort this used to perform was already inert and has been
 * dropped rather than carried forward as a misleading second ordering rule.
 * @param cards - Array of all kanban cards to determine positioning
 * @param toPhaseId - Target phase ID where the card is being moved
 * @param insertionIndex - Optional specific position index in target phase (if undefined, card goes to end)
 * @returns Position number for the card in target phase, capped at the phase's current card count
 */
export const calculateNewPosition = (
  cards: KanbanCard[],
  toPhaseId: string,
  insertionIndex?: number
): number => {
  const targetPhaseCardCount = cards.filter(
    c => c.kanbanPhase.id === toPhaseId
  ).length;

  if (insertionIndex !== undefined && insertionIndex >= 0) {
    return Math.min(insertionIndex, targetPhaseCardCount);
  }

  return targetPhaseCardCount;
};
